/**
 * ตัวเลือก "ช่วงวันที่" แบบปฏิทิน 2 เดือนคู่ — เลือกวันเริ่ม แล้วเลือกวันสิ้นสุด
 * (จอแคบยุบเหลือเดือนเดียว เลื่อนเดือนด้วยลูกศร).
 *
 * กติกาที่ตั้งใจ:
 *  - ค่าที่เลือกยังไม่มีผลจนกว่าจะกด "ตกลง" — กันหน้าที่ดึงข้อมูลหนักยิงซ้ำ
 *    ทุกครั้งที่แตะวัน (ปฏิทินเวรดึง คน × วัน ทั้งก้อน)
 *  - แตะวันแรก = วันเริ่ม, แตะวันที่สอง = วันสิ้นสุด; แตะวันก่อนหน้าวันเริ่ม =
 *    เริ่มนับใหม่จากวันนั้น (ไม่ต้องกดล้างก่อน)
 *  - `maxDays` = เพดานจำนวนวันที่ backend ยอมให้ดึงครั้งเดียว — บอกบนจอตั้งแต่
 *    ตอนเลือก ไม่ปล่อยให้กดตกลงแล้วค่อยเด้ง error
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

const TH_MONTHS = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
const TH_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const DOW_HEAD = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']; // index = getDay()

export interface DateRange {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
}

const pad = (n: number) => String(n).padStart(2, '0');
export const isoOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseIso = (s: string) => new Date(`${s}T00:00:00`);

/** "2026-08-01" → "1 ส.ค. 2569" */
export function thaiShortDate(s: string): string {
  if (!s) return '—';
  const d = parseIso(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${d.getDate()} ${TH_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear() + 543}`;
}

/** จำนวนวันรวมหัวรวมท้าย (ต้องตรงกับ rangeDayCount ฝั่ง worker) */
export function countDays(from: string, to: string): number {
  const a = parseIso(from).getTime();
  const b = parseIso(to).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
  return Math.round((b - a) / 86_400_000) + 1;
}

/** ทุกวันในช่วง (รวมหัวท้าย) — ใช้วาดคอลัมน์ของตาราง */
export function eachDay(from: string, to: string): Date[] {
  const out: Date[] = [];
  const d = parseIso(from);
  const end = parseIso(to);
  while (d <= end) {
    out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

export function startOfMonthIso(d = new Date()): string {
  return isoOf(new Date(d.getFullYear(), d.getMonth(), 1));
}
export function endOfMonthIso(d = new Date()): string {
  return isoOf(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

/** ตารางวันของเดือนหนึ่ง (เริ่มสัปดาห์วันอาทิตย์) — null = ช่องว่างหัว/ท้ายเดือน */
function monthGrid(year: number, month: number): Array<Date | null> {
  const first = new Date(year, month, 1);
  const days = new Date(year, month + 1, 0).getDate();
  const lead = first.getDay();
  const cells: Array<Date | null> = Array.from({ length: lead }, () => null);
  for (let i = 1; i <= days; i++) cells.push(new Date(year, month, i));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function MonthPanel({
  year, month, from, to, onPick,
}: {
  year: number; month: number; from: string; to: string; onPick: (iso: string) => void;
}) {
  const cells = useMemo(() => monthGrid(year, month), [year, month]);
  const today = isoOf(new Date());
  return (
    <div className="min-w-[236px]">
      <div className="text-center text-sm font-semibold text-ink mb-2">
        {TH_MONTHS[month]} {year + 543}
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-[11px] text-warm-gray mb-1">
        {DOW_HEAD.map((d, i) => (
          <div key={d} className={`text-center py-0.5 ${i === 0 ? 'text-red-500' : ''}`}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={`x${i}`} />;
          const iso = isoOf(d);
          const isFrom = iso === from;
          const isTo = iso === to;
          const inRange = !!from && !!to && iso > from && iso < to;
          const edge = isFrom || isTo;
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onPick(iso)}
              className={[
                'h-8 rounded-lg text-xs transition-colors',
                edge ? 'bg-pink text-white font-semibold' : inRange ? 'bg-pink-100 text-pink-700' : 'hover:bg-gray-100 text-ink',
                !edge && iso === today ? 'ring-1 ring-pink/50' : '',
              ].join(' ')}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DateRangePicker({
  value,
  onChange,
  maxDays,
  label,
  presets = true,
}: {
  value: DateRange;
  onChange: (r: DateRange) => void;
  maxDays?: number;
  label?: string;
  /** ปุ่มลัด เดือนนี้ / เดือนหน้า (ปิดได้เมื่อหน้าไหนไม่ต้องการ) */
  presets?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(value);
  // เดือนซ้ายของแผงคู่ — เปิดครั้งแรกให้ตรงกับเดือนของวันเริ่มที่เลือกอยู่
  const [cursor, setCursor] = useState(() => parseIso(value.from || isoOf(new Date())));
  const boxRef = useRef<HTMLDivElement>(null);

  // เปิดใหม่ทุกครั้ง = เริ่มจากค่าที่ใช้อยู่จริง (ทิ้ง draft ค้างของรอบก่อน)
  useEffect(() => {
    if (open) {
      setDraft(value);
      setCursor(parseIso(value.from || isoOf(new Date())));
    }
  }, [open, value.from, value.to]);

  // คลิกนอกกล่อง / กด Esc = ปิดโดยไม่บันทึก
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function pick(iso: string) {
    setDraft((d) => {
      // ยังไม่ได้เลือกครบคู่ (to ว่าง) และวันใหม่อยู่หลัง from → ปิดช่วง
      if (d.from && !d.to && iso >= d.from) return { from: d.from, to: iso };
      // เลือกครบคู่แล้ว หรือแตะวันก่อน from → เริ่มนับใหม่จากวันนี้
      return { from: iso, to: '' };
    });
  }

  const draftDays = draft.from && draft.to ? countDays(draft.from, draft.to) : 0;
  const overLimit = !!maxDays && draftDays > maxDays;
  const canApply = !!draft.from && !!draft.to && !overLimit;

  const leftYear = cursor.getFullYear();
  const leftMonth = cursor.getMonth();
  const right = new Date(leftYear, leftMonth + 1, 1);

  function applyPreset(kind: 'this' | 'next') {
    const base = new Date();
    const m = kind === 'this' ? base.getMonth() : base.getMonth() + 1;
    const from = isoOf(new Date(base.getFullYear(), m, 1));
    const to = isoOf(new Date(base.getFullYear(), m + 1, 0));
    setDraft({ from, to });
    setCursor(parseIso(from));
  }

  return (
    <div className="relative" ref={boxRef}>
      {label && <label className="label">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input flex items-center gap-2 text-left"
      >
        <CalendarDays className="w-4 h-4 text-pink shrink-0" />
        <span className="truncate whitespace-nowrap">
          {thaiShortDate(value.from)} – {thaiShortDate(value.to)}
        </span>
      </button>

      {open && (
        <div className="absolute z-40 mt-1 card p-3 shadow-lg max-w-[calc(100vw-2rem)] overflow-auto">
          {/* หัวแผง: เลื่อนเดือน + ปุ่มลัด */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <button
              type="button"
              onClick={() => setCursor(new Date(leftYear, leftMonth - 1, 1))}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
              title="เดือนก่อน"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {presets && (
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => applyPreset('this')} className="btn-secondary btn-slim">เดือนนี้</button>
                <button type="button" onClick={() => applyPreset('next')} className="btn-secondary btn-slim">เดือนหน้า</button>
              </div>
            )}
            <button
              type="button"
              onClick={() => setCursor(new Date(leftYear, leftMonth + 1, 1))}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
              title="เดือนถัดไป"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="flex gap-4">
            <MonthPanel year={leftYear} month={leftMonth} from={draft.from} to={draft.to} onPick={pick} />
            {/* จอแคบ (มือถือ) โชว์เดือนเดียว — เลื่อนด้วยลูกศรแทน */}
            <div className="hidden sm:block">
              <MonthPanel year={right.getFullYear()} month={right.getMonth()} from={draft.from} to={draft.to} onPick={pick} />
            </div>
          </div>

          {/* สรุปช่วงที่กำลังจะเลือก + เตือนเกินเพดานตั้งแต่ยังไม่กดตกลง */}
          <div className="mt-2 text-xs text-warm-gray">
            {draft.from && !draft.to && <span>เลือกวันสิ้นสุด (วันเริ่ม {thaiShortDate(draft.from)})</span>}
            {draft.from && draft.to && (
              <span className={overLimit ? 'text-red-600' : ''}>
                {thaiShortDate(draft.from)} – {thaiShortDate(draft.to)} · {draftDays} วัน
                {overLimit ? ` — เกินเพดาน ${maxDays} วัน ต้องแบ่งดูเป็นช่วงย่อย` : ''}
              </span>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 mt-3">
            <button type="button" onClick={() => setDraft({ from: '', to: '' })} className="btn-secondary btn-slim">
              ล้างข้อมูล
            </button>
            <button
              type="button"
              disabled={!canApply}
              onClick={() => { onChange(draft); setOpen(false); }}
              className="btn-primary btn-slim disabled:opacity-40"
            >
              ตกลง
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
